import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslationWithPrefix } from '@/i18n/hooks';
import GeneralInformationTab from '@/vacancy_request/components/template-request-form/general-information-tab';
import NavigationButtons from '@/vacancy_request/components/template-request-form/navigation-buttons';
import VacancyInformationTab from '@/vacancy_request/components/template-request-form/vacancy-information-tab';
import ApplicantInformationTab from '@/vacancy_request/components/template-request-form/applicant-information-tab';
import { TabItem } from '@/vacancy_request/components/template-request-form/navigation-buttons/styled';
import { HrFlexContainer, HrPrimary, HrRed, HrWhite } from '@components/styles/hr-styles';
import {
  useDraftTemplateRequest,
  useTemplateMutation
} from '@/vacancy_request/components/template-request-form/hooks/use-requests';
import { serializeTemplateFormPayload } from '@/vacancy_request/components/template-request-form/utils';
import { useToast } from '@/hook/useToast';
import { AVAILABLE_FORM_MODES } from '@/vacancy_request/components/template-request-form/constants';

export const TemplateRequestForm = ({ mode, formValues, couldBeDraft }) => {
  const REDIRECT_PATH = '/app/configuration/vacancy_request';
  const { t } = useTranslationWithPrefix('requestVacancies.requestTemplate.form');
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitTypeRef = useRef(null);
  const redirectTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) window.clearTimeout(redirectTimeoutRef.current);
    };
  }, []);

  if (!AVAILABLE_FORM_MODES.includes(mode)) {
    throw new Error(`Mode ${mode} is not available. Available modes are: ${AVAILABLE_FORM_MODES.join(', ')}`);
  }

  const formMethods = useForm({
    mode: 'onTouched',
    reValidateMode: 'onSubmit',
    defaultValues: formValues
  });
  
  const { handleSubmit, getValues, setError, clearErrors, trigger } = formMethods;

  const tabs = [
    { id: 0, label: t('tabs.general') },
    { id: 1, label: t('tabs.vacancyDetails') },
    { id: 2, label: t('tabs.candidateDetails') }
  ];

  const contentTab = () => {
    return (
      <>
        <div style={{ display: activeTab === 0 ? 'contents' : 'none' }}>
          <GeneralInformationTab disabled={mode === 'view'} />
        </div>
        <div style={{ display: activeTab === 1 ? 'contents' : 'none' }}>
          <VacancyInformationTab disabled={mode === 'view'} />
        </div>
        <div style={{ display: activeTab === 2 ? 'contents' : 'none' }}>
          <ApplicantInformationTab disabled={mode === 'view'} />
        </div>
      </>
    );
  };

  const onShowToast = (toastConfiguration) => {
    const isError = toastConfiguration?.type === 'error' || toastConfiguration?.type === 'warning';
    showToast({
      message: toastConfiguration?.message || '',
      title: toastConfiguration?.title || '',
      iconClassName: isError ? 'hr-Alert' : 'hr-Checkmark',
      color: isError ? HrRed : HrPrimary
    });
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const onNavigateForward = () => {
    setActiveTab(activeTab + 1);
  };

  const onNavigateBack = () => {
    setActiveTab(activeTab - 1);
  };

  const onErrorForm = (errors) => {
    onShowToast({ visible: true, type: 'error', message: 'Revise que todos los campos esten completos' });
  };

  const onRequestSuccess = () => {
    const taostMessage = mode === 'create' ? t('messages.creationSuccess') : t('messages.updatingSuccess');

    onShowToast({
      visible: true,
      type: 'success',
      message: taostMessage
    });

    if (submitTypeRef.current === 'template' || submitTypeRef.current === 'draft') {
      redirectTimeoutRef.current = window.setTimeout(() => {
        window.location.assign(REDIRECT_PATH);
      }, 1200);
    }
  };

  const onRequestError = (error) => {
    console.error('error', error);

    const fieldErrors = Array.isArray(error?.message) ? error.message : [];
    const fieldTabMap = {
      attachedFile: 2
    };
    let hasMappedFieldError = false;

    fieldErrors.forEach((fieldError) => {
      if (!fieldError?.param) return;

      const isRequiredError = typeof fieldError?.msg === 'string' && fieldError.msg.includes('is required by template');
      const message = isRequiredError ? 'Campo obligatorio' : fieldError.msg;

      setError(fieldError.param, {
        type: 'server',
        shouldFocus: true,
        message
      });

      if (!hasMappedFieldError && fieldTabMap[fieldError.param] !== undefined) {
        setActiveTab(fieldTabMap[fieldError.param]);
        hasMappedFieldError = true;
      }
    });

    onShowToast({
      visible: true,
      type: 'error',
      message: t('messages.error.requestError')
    });
  };

  const storeTemplateRequest = useTemplateMutation({
    onHandleSubmitting: setIsSubmitting,
    onRequestSuccess,
    onRequestError,
    mode
  });

  const storeDraftTemplateRequest = useDraftTemplateRequest({
    onHandleSubmitting: setIsSubmitting,
    onRequestSuccess,
    onRequestError,
    mode
  });

  const onSubmit = (data) => {
    clearErrors();
    submitTypeRef.current = 'template';
    storeTemplateRequest.mutate(serializeTemplateFormPayload(data));
  };

  const onSubmitDraft = async () => {
    const isDraftMinimumValid = await trigger(['template_name', 'templateAreaId', 'templateClientId']);

    if (!isDraftMinimumValid) {
      setActiveTab(0);
      return;
    }

    clearErrors();
    submitTypeRef.current = 'draft';
    const currentFormValues = getValues();
    storeDraftTemplateRequest.mutate(serializeTemplateFormPayload(currentFormValues));
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit, onErrorForm)}>
        <HrFlexContainer flexDirection='column' gap='24px'>
          {/* Navegación de tabs */}
          <HrFlexContainer justifyContent='space-evenly' backgroundColor={HrWhite}>
            {tabs.map((tab, tabIndex) => (
              <TabItem key={tab.id} className={activeTab <= 3 ? 'active' : ''} onClick={() => handleTabChange(tab.id)}>
                <p>{`${tabIndex + 1}. ${tab.label}`}</p>
                <div className={activeTab === tab.id ? 'active' : ''} />
              </TabItem>
            ))}
          </HrFlexContainer>

          {/* Contenido del tab activo */}
          <HrFlexContainer flexDirection='column' gap='32px' padding='48px 24px' backgroundColor={HrWhite}>
            {contentTab()}

            {/* Botones de navegación */}
            <NavigationButtons
              activeTab={activeTab}
              onNavigateForward={onNavigateForward}
              onNavigateBack={onNavigateBack}
              isSubmitting={isSubmitting}
              mode={mode}
              canStoreDraftVacancy={couldBeDraft}
              submitDraft={onSubmitDraft}
            />
          </HrFlexContainer>
        </HrFlexContainer>
      </form>
    </FormProvider>
  );
};

TemplateRequestForm.propTypes = {
  mode: PropTypes.string.isRequired,
  formValues: PropTypes.object.isRequired,
  couldBeDraft: PropTypes.bool.isRequired
};
